/**
 * Persist an in-progress Cook Mode deep link so cold starts (PWA start_url "/plan")
 * can restore the session instead of landing on a spinner with no recipe id.
 */

export const COOK_MODE_RESUME_KEY = 'cook_mode_resume_v1'

/**
 * @typedef {object} CookModeResumeState
 * @property {string} recipeId
 * @property {string|null} [sessionId]
 * @property {string|null} [planItemId]
 * @property {number} [updatedAt]
 */

/** @returns {CookModeResumeState|null} */
export function peekCookModeResume() {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(COOK_MODE_RESUME_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const recipeId = typeof parsed.recipeId === 'string' ? parsed.recipeId.trim() : ''
    if (!recipeId) return null
    return {
      recipeId,
      sessionId:
        typeof parsed.sessionId === 'string' && parsed.sessionId.trim()
          ? parsed.sessionId.trim()
          : null,
      planItemId:
        typeof parsed.planItemId === 'string' && parsed.planItemId.trim()
          ? parsed.planItemId.trim()
          : null,
      updatedAt: Number.isFinite(Number(parsed.updatedAt)) ? Number(parsed.updatedAt) : undefined,
    }
  } catch {
    return null
  }
}

/**
 * @param {{ recipeId: string, sessionId?: string|null, planItemId?: string|null }} state
 */
export function saveCookModeResume(state) {
  if (typeof localStorage === 'undefined') return
  const recipeId = typeof state?.recipeId === 'string' ? state.recipeId.trim() : ''
  if (!recipeId) return
  try {
    /** @type {CookModeResumeState} */
    const payload = {
      recipeId,
      sessionId:
        typeof state.sessionId === 'string' && state.sessionId.trim()
          ? state.sessionId.trim()
          : null,
      planItemId:
        typeof state.planItemId === 'string' && state.planItemId.trim()
          ? state.planItemId.trim()
          : null,
      updatedAt: Date.now(),
    }
    localStorage.setItem(COOK_MODE_RESUME_KEY, JSON.stringify(payload))
  } catch {
    // Ignore quota / private-mode errors.
  }
}

export function clearCookModeResume() {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(COOK_MODE_RESUME_KEY)
  } catch {
    // Ignore.
  }
}

/** @returns {CookModeResumeState|null} */
export function consumeCookModeResume() {
  const state = peekCookModeResume()
  clearCookModeResume()
  return state
}

/**
 * Build the cook-mode path+search for a resume payload.
 * @param {CookModeResumeState} state
 */
export function buildCookModeResumeHref(state) {
  if (!state?.recipeId) return null
  const params = new URLSearchParams({ tab: 'cook', id: state.recipeId })
  if (state.sessionId) params.set('session', state.sessionId)
  if (state.planItemId) params.set('planItemId', state.planItemId)
  return `/recipes?${params.toString()}`
}
